import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogClose,
} from "../components/ui/dialog";
import { adminApi, publicApi } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { formatCurrency, truncate } from "../lib/utils";
import { toast } from "sonner";
import { Search, Loader2, AlertTriangle, Package, Filter } from "lucide-react";

export default function InventoryPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [stockFilter, setStockFilter] = useState("all");
  const [origins, setOrigins] = useState([]);
  const [originFilter, setOriginFilter] = useState("all");

  // Edit stock modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [editVariantStocks, setEditVariantStocks] = useState([]);
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    fetchData();
    fetchOrigins();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await adminApi.getProducts({ search: search || undefined });
      setProducts(res.data);
    } catch (error) {
      toast.error("Failed to load inventory");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrigins = async () => {
    try {
      const res = await publicApi.getOrigins();
      setOrigins(res.data);
    } catch (error) {
      console.error("Failed to load origins");
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchData();
  };

  const filteredProducts = products.filter((product) => {
    let passes = true;

    if (stockFilter === "low") {
      passes = product.total_stock < 5;
    } else if (stockFilter === "out") {
      passes = product.total_stock === 0;
    } else if (stockFilter === "in") {
      passes = product.total_stock >= 5;
    }

    if (
      originFilter &&
      originFilter !== "all" &&
      product.origin_id !== originFilter
    ) {
      passes = false;
    }

    return passes;
  });

  const lowStockCount = products.filter(
    (p) => p.total_stock < 5 && p.total_stock > 0,
  ).length;
  const outOfStockCount = products.filter((p) => p.total_stock === 0).length;
  const totalStock = products.reduce((sum, p) => sum + p.total_stock, 0);

  // Open edit modal
  const handleEditStock = (product) => {
    setEditProduct(product);
    // Prepare editable stocks for each color variant
    setEditVariantStocks(
      (product.color_variants || []).map((variant) => ({
        ...variant,
        stock: variant.stock || 0,
      })),
    );
    setEditModalOpen(true);
  };

  // Save stock update
  const handleSaveStock = async () => {
    if (!editProduct) return;
    setEditLoading(true);
    try {
      // Prepare updated color_variants array
      const updatedVariants = editVariantStocks.map(
        ({ _id, color_id, color_name, hex_code, images, stock }) => ({
          _id,
          color_id,
          color_name,
          hex_code,
          images,
          stock: Number(stock) || 0,
        }),
      );
      await adminApi.updateProduct(editProduct.id, {
        color_variants: updatedVariants,
      });
      toast.success("Stock updated");
      setEditModalOpen(false);
      fetchData();
    } catch (e) {
      toast.error("Failed to update stock");
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="inventory-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl text-primary font-semibold">
          Inventory
        </h1>
        <p className="text-muted-foreground mt-1">
          Track stock levels across your products
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Products</p>
                <p className="text-2xl font-heading font-bold text-primary">
                  {products.length}
                </p>
              </div>
              <Package className="w-8 h-8 text-primary/20" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Stock</p>
                <p className="text-2xl font-heading font-bold text-primary">
                  {totalStock}
                </p>
              </div>
              <Package className="w-8 h-8 text-green-500/20" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Low Stock</p>
                <p className="text-2xl font-heading font-bold text-orange-500">
                  {lowStockCount}
                </p>
              </div>
              <AlertTriangle className="w-8 h-8 text-orange-500/20" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Out of Stock</p>
                <p className="text-2xl font-heading font-bold text-red-500">
                  {outOfStockCount}
                </p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500/20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="card-base">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search products..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 input-base"
                data-testid="search-inventory-input"
              />
            </div>
            <Select value={stockFilter} onValueChange={setStockFilter}>
              <SelectTrigger className="w-40 input-base">
                <SelectValue placeholder="Stock level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stock</SelectItem>
                <SelectItem value="low">Low Stock (&lt;5)</SelectItem>
                <SelectItem value="out">Out of Stock</SelectItem>
                <SelectItem value="in">In Stock</SelectItem>
              </SelectContent>
            </Select>
            <Select value={originFilter} onValueChange={setOriginFilter}>
              <SelectTrigger className="w-40 input-base">
                <SelectValue placeholder="All Origins" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Origins</SelectItem>
                {origins.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="submit"
              variant="outline"
              className="border-primary text-primary"
            >
              <Filter className="w-4 h-4 mr-2" />
              Apply
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Inventory Table */}
      <Card className="card-base">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead className="w-20">Image</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Origin</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Color Variants</TableHead>
                    <TableHead>Total Stock</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Edit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProducts.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="text-center py-8 text-muted-foreground"
                      >
                        No products match the filters
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredProducts.map((product) => (
                      <TableRow key={product.id} className="hover:bg-canvas/50">
                        <TableCell>
                          {product.color_variants?.[0]?.images?.[0] ? (
                            <img
                              src={`${product.color_variants[0].images[0]}`}
                              alt={product.name}
                              className="w-12 h-12 object-cover rounded-md"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-muted rounded-md flex items-center justify-center">
                              <Package className="w-5 h-5 text-muted-foreground" />
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-primary">
                              {truncate(product.name, 30)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {product.weave_type_name}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {product.origin_name}
                        </TableCell>
                        <TableCell className="font-semibold">
                          {formatCurrency(product.price)}
                          {product.discount_percentage > 0 && (
                            <span className="block text-xs text-green-600">
                              {product.discount_percentage}% off
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {product.color_variants?.map((variant, idx) => (
                              <div
                                key={idx}
                                className="flex items-center gap-2 text-xs"
                              >
                                <div
                                  className="w-3 h-3 rounded-full border"
                                  style={{ backgroundColor: variant.hex_code }}
                                />
                                <span className="text-muted-foreground">
                                  {variant.color_name || "N/A"}
                                </span>
                                <Badge
                                  variant="outline"
                                  className={
                                    variant.stock === 0
                                      ? "badge-error"
                                      : variant.stock < 5
                                        ? "badge-pending"
                                        : "badge-success"
                                  }
                                >
                                  {variant.stock}
                                </Badge>
                              </div>
                            ))}
                            {(!product.color_variants ||
                              product.color_variants.length === 0) && (
                              <span className="text-xs text-muted-foreground">
                                No variants
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-bold text-lg">
                            {product.total_stock}
                          </span>
                        </TableCell>
                        <TableCell>
                          {product.total_stock === 0 ? (
                            <Badge className="badge-error">Out of Stock</Badge>
                          ) : product.total_stock < 5 ? (
                            <Badge className="badge-pending">Low Stock</Badge>
                          ) : (
                            <Badge className="badge-success">In Stock</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditStock(product)}
                          >
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>

              {/* Edit Stock Modal */}
              <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Edit Stock</DialogTitle>
                  </DialogHeader>
                  <div className="mb-4 space-y-4">
                    {editVariantStocks.length === 0 ? (
                      <div className="text-muted-foreground text-sm">
                        No color variants for this product.
                      </div>
                    ) : (
                      editVariantStocks.map((variant, idx) => (
                        <div
                          key={variant._id || idx}
                          className="flex items-center gap-4"
                        >
                          <div
                            className="w-4 h-4 rounded-full border"
                            style={{ backgroundColor: variant.hex_code }}
                          />
                          <span className="flex-1">
                            {variant.color_name || "N/A"}
                          </span>
                          <Input
                            type="number"
                            min={0}
                            value={variant.stock}
                            onChange={(e) => {
                              const newStocks = [...editVariantStocks];
                              newStocks[idx].stock = e.target.value;
                              setEditVariantStocks(newStocks);
                            }}
                            className="input-base w-24"
                          />
                        </div>
                      ))
                    )}
                  </div>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button
                        variant="outline"
                        onClick={() => setEditModalOpen(false)}
                        disabled={editLoading}
                      >
                        Cancel
                      </Button>
                    </DialogClose>
                    <Button
                      onClick={handleSaveStock}
                      loading={editLoading}
                      disabled={
                        editLoading ||
                        editVariantStocks.some(
                          (v) => isNaN(Number(v.stock)) || Number(v.stock) < 0,
                        )
                      }
                    >
                      Save
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
